/**
 * Канонічний телефонний OTP-флоу (REFACTOR_PLAN п.17, етап 2).
 *
 * До цього логіка існувала у ДВОХ розбіжних копіях — `stores/auth.ts` (вхід) і
 * `components/auth/PhoneOtpModal.vue` (зміна номера в профілі) — з різними
 * багами в кожній:
 *
 *  - модалка тримала СТАТИЧНИЙ контейнер `id="recaptcha-verify-phone"` і
 *    перевикористовувала його між відправками; стор на кожну відправку робив
 *    свіжий off-screen контейнер саме тому, що iOS Safari на повторному
 *    використанні віддає «reCAPTCHA has already been rendered»;
 *  - модалка мала ДВА захардкоджені тексти помилок замість мапінгу;
 *  - модалка читала для Sentry самий `e.code`, тож тег `firebase_code`
 *    для цього флоу часто лишався порожнім.
 *
 * Тепер мéханіка одна, а різниця між флоу — рівно дві фінальні дії, які
 * приходять параметрами: `send` і `confirm`.
 *
 * Лайфсайкл-хуків тут НЕМАЄ навмисно: композабл викликається і з setup
 * компонента, і з setup Pinia-стора, де `onMounted` не працює. Секундний
 * тікер для зворотного відліку лишається на стороні UI — його текст у входу
 * і в модалці різний, і це презентація, а не механіка.
 */
import { ref, shallowRef } from "vue";
import { RecaptchaVerifier } from "firebase/auth";
import type { Auth } from "firebase/auth";
import * as Sentry from "@sentry/nuxt";
import { useNuxtApp } from "#app";
import { allowOtpSend, markOtpSent } from "~/utils/otpThrottle";
import type { OtpAllowance } from "~/utils/otpThrottle";
import {
  PhoneAuthError,
  friendlyPhoneErrorMessage,
  getFirebaseAuthCode,
  extractPhoneAuthError,
  shouldResetConfirmation,
} from "~/utils/phoneAuthErrors";

/** Розділяє два флоу в Sentry. Без цього вони зливались в одну купу. */
export type PhoneAuthFlow = "signin" | "phone_update";

// ============================================================
// reCAPTCHA: ЄДИНА реалізація на проєкт
// ============================================================
let _recaptcha: RecaptchaVerifier | null = null;
let _recaptchaContainerId: string | null = null;

function removeRecaptchaContainer(): void {
  if (import.meta.server) return;
  if (_recaptchaContainerId) {
    const el = document.getElementById(_recaptchaContainerId);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }
  _recaptchaContainerId = null;
}

/**
 * Свіжий off-screen контейнер на КОЖНУ відправку.
 *
 * iOS Safari особливо болісно реагує на перевикористання контейнера й
 * верифаєра — саме на цьому модалка ловила «already been rendered».
 */
function createFreshRecaptchaContainer(): string | null {
  if (import.meta.server) return null;

  removeRecaptchaContainer();

  const id = `recaptcha-container-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;

  const el = document.createElement("div");
  el.id = id;
  el.style.position = "fixed";
  el.style.left = "-9999px";
  el.style.top = "-9999px";
  el.style.width = "0";
  el.style.height = "0";
  el.setAttribute("aria-hidden", "true");
  document.body.appendChild(el);

  _recaptchaContainerId = id;
  return id;
}

/** Best-effort прибирання верифаєра і його контейнера. */
export function clearRecaptcha(): void {
  try {
    _recaptcha?.clear?.();
  } catch {
    /* верифаєр уже міг бути знесений SDK */
  }
  _recaptcha = null;
  removeRecaptchaContainer();
}

/**
 * Єдине місце в проєкті, де створюється `RecaptchaVerifier`.
 *
 * `render()` і `verify()` тут НЕ викликаються навмисно: Firebase рендерить
 * віджет ліниво сам — `RecaptchaVerifier.verify()` починається з
 * `await this.render()`, — а ручний виклик лише завчасно вантажить
 * `recaptcha/api.js` у `window.grecaptcha` (CLAUDE.md, заборона 2).
 */
function createRecaptcha($auth: Auth, flow: PhoneAuthFlow): RecaptchaVerifier {
  clearRecaptcha();
  const containerId = createFreshRecaptchaContainer();

  try {
    _recaptcha = new RecaptchaVerifier($auth, containerId as string, {
      size: "invisible",
    });
    return _recaptcha;
  } catch (e) {
    capturePhoneAuthError(flow, "recaptcha_init", e, {
      containerId: String(containerId || ""),
    });
    throw e;
  }
}

// ============================================================
// Телеметрія
// ============================================================
/** Детект in-app-браузерів (Instagram, Facebook, TikTok…). */
export function isInAppBrowser(): boolean {
  if (import.meta.server) return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Line\/|Twitter|Snapchat|TikTok|BytedanceWebview/i.test(
    ua,
  );
}

/**
 * ЄДИНИЙ capture телефонного флоу.
 *
 * НІКОЛИ не кладе сюди номер телефону або код з SMS. Сторінки/компоненти
 * capture НЕ дублюють: дубль створював другу групу на кожне падіння,
 * згруповану за українським текстом, і подвоював лічильники.
 */
export function capturePhoneAuthError(
  flow: PhoneAuthFlow,
  step: string,
  err: unknown,
  extra: Record<string, unknown> = {},
): void {
  try {
    const raw = extractPhoneAuthError(err);
    const code = String(getFirebaseAuthCode(err) || "");

    Sentry.withScope((scope) => {
      scope.setTag("area", "auth");
      scope.setTag("auth_method", "phone");
      scope.setTag("auth_flow", flow);
      scope.setTag("auth_step", String(step || ""));
      if (code) scope.setTag("firebase_code", code);
      scope.setLevel("error");

      scope.setContext("firebase", { code, message: String(raw || "") });

      Object.keys(extra || {}).forEach((k) => {
        try {
          scope.setExtra(k, extra[k]);
        } catch {
          /* несеріалізовне значення — не привід втратити подію */
        }
      });

      Sentry.captureException(err);
    });
  } catch {
    /* телеметрія не має права ламати вхід */
  }
}

/** Текст для впертого в гальмо. Розрізняє паузу і вичерпану годинну стелю. */
export function otpThrottleMessage(allowance: OtpAllowance): string {
  if (allowance.allowed) return "";
  if (allowance.reason === "window") {
    const min = Math.max(1, Math.ceil(allowance.retryAfterSec / 60));
    return `Забагато кодів на цей номер. Спробуйте за ${min} хв.`;
  }
  return `Наступний код можна надіслати через ${allowance.retryAfterSec} с.`;
}

// ============================================================
// Композабл
// ============================================================
export interface PhoneOtpConfig<H, R> {
  flow: PhoneAuthFlow;
  /** Фінальна дія відправки — єдине, чим флоу різняться на цьому кроці. */
  send: (phoneE164: string, verifier: RecaptchaVerifier) => Promise<H>;
  /** Фінальна дія підтвердження — те саме для кроку коду. */
  confirm: (handle: H, code: string) => Promise<R>;
}

export function usePhoneOtp<H, R>(config: PhoneOtpConfig<H, R>) {
  const { $auth } = useNuxtApp() as { $auth: Auth };

  /** Результат `send`: ConfirmationResult для входу, verificationId для зміни. */
  const handle = shallowRef<H | null>(null);
  const sending = ref(false);
  const confirming = ref(false);
  const sent = ref(false);
  /** Невдалі підтвердження поспіль — UI вирішує, коли здатись. */
  const attempts = ref(0);

  /**
   * Одна спроба — і крапка. Ретрай тут був шкідливий: на кожен повтор ішов ще
   * один реальний `sendVerificationCode`, а Firebase рахує їх у ліміті на
   * номер. Чотири спроби × два запити = до восьми звернень на ОДНЕ натискання
   * кнопки, після чого номер отримував `auth/too-many-requests` на години.
   */
  async function sendCode(phoneE164: string): Promise<void> {
    if (import.meta.server) return;
    if (!phoneE164) {
      throw new PhoneAuthError(
        "phone/invalid-format",
        "Некоректний номер телефону.",
      );
    }

    // Гальмо ТУТ, а не в кожного споживача: раніше кожен гейтив сам, і будь-яка
    // нова точка відправки легко про нього забувала (CLAUDE.md, заборона 4).
    // Перевіряємо сховище, а не computed: між тіком таймера і кліком могла
    // проскочити відправка з іншої вкладки на той самий номер.
    const allowance = allowOtpSend(phoneE164);
    if (!allowance.allowed) {
      throw new PhoneAuthError(
        "otp/throttled",
        otpThrottleMessage(allowance),
        allowance,
      );
    }

    sending.value = true;
    try {
      // createRecaptcha() мусить бути ВСЕРЕДИНІ try: якщо конструктор
      // верифаєра кине (а він кидає — саме тому там свій capture), то при
      // виклику ззовні `sending` лишався б true назавжди і замикав усі
      // кнопки модалки, включно зі «Скасувати».
      const verifier = createRecaptcha($auth, config.flow);
      handle.value = await config.send(phoneE164, verifier);
      // Рахуємо лише ВДАЛУ відправку: якщо SMS не пішла, гальмувати нема за що.
      markOtpSent(phoneE164);
      sent.value = true;
      attempts.value = 0;
    } catch (e) {
      capturePhoneAuthError(config.flow, "send_code", e, {
        hasHandle: !!handle.value,
        isInAppBrowser: isInAppBrowser(),
      });
      throw new PhoneAuthError(
        getFirebaseAuthCode(e),
        friendlyPhoneErrorMessage(e, isInAppBrowser()),
        e,
      );
    } finally {
      sending.value = false;
      // Верифаєр і контейнер більше не потрібні: SDK викликає `_reset()` сам,
      // а контейнер лишається в DOM, поки ми його не приберемо.
      clearRecaptcha();
    }
  }

  async function confirmCode(code: unknown): Promise<R> {
    if (!handle.value) {
      throw new PhoneAuthError(
        "phone/no-confirmation",
        "Спочатку надішліть код",
      );
    }

    // Код з SMS НІКОЛИ не логується і нікуди не передається.
    const sms = String(code || "").trim();
    if (!sms) throw new PhoneAuthError("phone/empty-code", "Введіть код з SMS");

    confirming.value = true;
    try {
      const result = await config.confirm(handle.value, sms);
      handle.value = null;
      sent.value = false;
      attempts.value = 0;
      clearRecaptcha();
      return result;
    } catch (e) {
      const willReset = shouldResetConfirmation(e);
      capturePhoneAuthError(config.flow, "confirm_code", e, {
        hasHandle: !!handle.value,
        willResetConfirmation: willReset,
      });
      attempts.value += 1;
      // Сесія протухла — новий код обов'язковий, старий handle вже нічого не варт.
      if (willReset) {
        handle.value = null;
        sent.value = false;
      }
      throw new PhoneAuthError(
        getFirebaseAuthCode(e),
        friendlyPhoneErrorMessage(e, isInAppBrowser()),
        e,
      );
    } finally {
      confirming.value = false;
    }
  }

  /** Скинути транзієнтний стан (скасування, зміна номера). */
  function reset(): void {
    handle.value = null;
    sent.value = false;
    attempts.value = 0;
    clearRecaptcha();
  }

  return {
    handle,
    sending,
    confirming,
    sent,
    attempts,
    sendCode,
    confirmCode,
    reset,
  };
}
